import { useCallback, type InputHTMLAttributes } from "react";
import { cn } from "tailwind-variants";

import { isUserLoggedIn } from "@design-lab/entities/user";
import { CanvaButton } from "@design-lab/features/upload-artwork/ui/Canva/CanvaButton";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button } from "@design-lab/shared/ui";

type UploadButtonsProps = {
  shouldOpenCanva?: boolean;
  onCanvaAutoOpenComplete?: (() => void) | undefined;
  onDeviceClick: () => void;
  getInputProps: (
    props?: InputHTMLAttributes<HTMLInputElement>
  ) => InputHTMLAttributes<HTMLInputElement>;
  onLibraryClick: () => void;
  onCanvaFileReady?: ((file: File) => Promise<void>) | undefined;
  isMobileOverlay?: boolean;
};

export function UploadButtons({
  shouldOpenCanva = false,
  onCanvaAutoOpenComplete = () => {},
  onDeviceClick,
  getInputProps,
  onLibraryClick,
  onCanvaFileReady,
  isMobileOverlay = false
}: UploadButtonsProps) {
  const isLoggedIn = isUserLoggedIn();
  const { isDesktop } = useResponsive();

  const handleCanvaFilesReady = useCallback(
    async (files: File[]) => {
      if (files[0] && onCanvaFileReady) {
        await onCanvaFileReady(files[0]);
      }
    },
    [onCanvaFileReady]
  );

  const buttonWidth = isMobileOverlay ? "h-9 w-[146px] md:h-10 md:w-48" : "w-full";

  return (
    <div className="flex w-full flex-col gap-1">
      <input
        {...getInputProps({
          "aria-label": "Upload artwork file",
          "data-testid": "artwork-file-input-canvas-overlay"
        })}
      />
      <Button
        onPress={onDeviceClick}
        className={
          cn(
            "text-background cursor-pointer rounded-lg bg-gray-900 px-4 text-xs font-bold md:px-6 md:text-base",
            buttonWidth
          )!
        }
        data-testid="my-device-button-canvas"
        size={isDesktop ? "xl" : "lg"}
      >
        Upload Image
      </Button>

      {isLoggedIn && (
        <>
          <Button
            onPress={onLibraryClick}
            variant="outline"
            size={isDesktop ? "xl" : "lg"}
            className={
              cn(
                "px-2 text-xs font-bold shadow-sm shadow-black/[0.1] md:px-4 md:text-base",
                buttonWidth
              )!
            }
            data-testid="my-library-button"
          >
            My Design Library
          </Button>

          {onCanvaFileReady && (
            <div className={cn("[&>button]:h-full [&>button]:w-full", buttonWidth)}>
              <CanvaButton
                autoOpen={shouldOpenCanva}
                onAutoOpenComplete={onCanvaAutoOpenComplete}
                onFilesReady={handleCanvaFilesReady}
                size={isDesktop ? "xl" : "lg"}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
